import React, { useId, useState } from "react";
import { Image as ImageIcon, X } from "lucide-react";
import { useT } from "../../lib/LanguageContext";
import { platformMediaSrc } from "../../lib/platformMedia";
import { HQ_SITE } from "../../constants/testIds/hqSite";
import siteApi from "./siteApi";
import PlatformMediaPicker from "./PlatformMediaPicker";

/**
 * An image slot backed by the platform media library: a preview, "Choose image" (opens
 * `PlatformMediaPicker`) and, when `onClear` is given, "Remove image".
 *
 * Controlled: `value` is what the owner currently holds, `onChange` receives the picked
 * `{ media_id, url }`. The owner decides when that becomes a write.
 *
 * @param {object} props
 * @param {string} props.label             visible label of the slot
 * @param {{ media_id: string | null, url: string } | null} props.value  `media_id` null:
 *   an image the owner shows that is not a library asset (an HQ blog post's legacy cover)
 * @param {(picked: { media_id: string, url: string }) => void} props.onChange
 * @param {() => void} [props.onClear]     omitted → no remove control
 * @param {string} [props.placeholderSrc]  shown when `value` is null (a default image)
 * @param {string} props.testId            base testid (see HQ_SITE.pickerField*)
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.readOnly]     the operator may not change it: the preview only
 * @param {typeof siteApi} [props.api]
 */
export default function MediaPickerField({
  label, value, onChange, onClear, placeholderSrc, testId, disabled = false, readOnly = false, api = siteApi,
}) {
  const t = useT("superadmin");
  const labelId = useId();
  const [open, setOpen] = useState(false);
  const previewSrc = value ? platformMediaSrc(value.url) : placeholderSrc;

  return (
    <div data-testid={HQ_SITE.pickerField(testId)}>
      <div id={labelId} className="text-xs text-slate-400 mb-1">{label}</div>
      <div className="flex items-center gap-3">
        <div className="w-20 h-14 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center flex-shrink-0">
          {previewSrc ? (
            <img src={previewSrc} alt="" className="w-full h-full object-cover" data-testid={HQ_SITE.pickerFieldPreview(testId)} />
          ) : (
            <ImageIcon className="w-5 h-5 text-slate-300" />
          )}
        </div>
        {!readOnly && (
        <div className="flex flex-col gap-1 items-start">
          <button
            type="button"
            disabled={disabled}
            onClick={() => setOpen(true)}
            aria-describedby={labelId}
            data-testid={HQ_SITE.pickerFieldChoose(testId)}
            className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50"
          >
            {value ? t("hsite.picker.replace") : t("hsite.picker.chooseImage")}
          </button>
          {onClear && value && (
            <button
              type="button"
              disabled={disabled}
              onClick={onClear}
              aria-describedby={labelId}
              data-testid={HQ_SITE.pickerFieldClear(testId)}
              className="text-xs inline-flex items-center gap-1 text-rose-500 hover:text-rose-600 disabled:opacity-50"
            >
              <X className="w-3 h-3" /> {t("hsite.picker.remove")}
            </button>
          )}
        </div>
        )}
      </div>
      {!readOnly && <PlatformMediaPicker open={open} onClose={() => setOpen(false)} onSelect={onChange} api={api} />}
    </div>
  );
}
